// Keeps a playing <video> on a target time taken from the scene clock.
// Small drift is closed with a playbackRate nudge, large drift with a seek.

const FRAME = 1 / 30;
const IN_SYNC = FRAME; // inside this: leave the video alone
const SETTLED = FRAME / 2; // a running nudge stops here
const SEEK_AT = 0.5; // beyond this: seek instead of nudging
const MAX_NUDGE = 0.1; // rate change limit (fraction of the base rate)
const FAST_NUDGE = 0.25; // limit while more than FAST_AT off (right after a start)
const FAST_AT = 2 * FRAME;
const CATCH_UP_SEC = 0.5; // wall time a nudge aims to close the drift in
const RATE_STEP = 0.02; // nudge granularity, so the rate is not rewritten every tick
const SEEK_AFTER_TICKS = 3;
const START_TOLERANCE = 0.2; // a paused video this close to the target just plays (no seek)
const START_LEAD = 0.1; // first guess of the delay between play() and the first frame
const MAX_LEAD = 0.4;
const START_WAIT_MS = 600; // longest wait for a pending play() before aligning again
// The presented frame jitters by about a frame (video fps against the display
// rate), and leaving the base rate costs the browser ~10 ms of playback. So
// nudges follow the averaged drift, not a single sample.
const SMOOTH_SEC = 0.15;

// The frame on screen, from requestVideoFrameCallback; falls back to
// currentTime (which runs ahead of the presented frame).
function presentedTime(sync, el) {
  if (!sync.watching && typeof el.requestVideoFrameCallback === "function") {
    sync.watching = true;
    const onFrame = (now, meta) => {
      if (sync.el !== el || !sync.watching) return;
      sync.frame = { mediaTime: meta.mediaTime, at: meta.expectedDisplayTime || now };
      sync.frameHandle = el.requestVideoFrameCallback(onFrame);
    };
    sync.frameHandle = el.requestVideoFrameCallback(onFrame);
  }
  const f = sync.frame;
  if (!f) return el.currentTime;
  // expectedDisplayTime can be a few ms ahead of now.
  const age = Math.max(0, (performance.now() - f.at) / 1000);
  // Stale after a seek or a pause.
  if (age > 0.25 || Math.abs(f.mediaTime - el.currentTime) > 0.5) return el.currentTime;
  return f.mediaTime + age * el.playbackRate;
}

// A forward seek must stay inside the buffered range; the stall gate waits
// for the rest. Backward seeks pass through.
export function clampSeekToBuffered(el, targetSec) {
  try {
    const b = el.buffered;
    if (!b || b.length === 0) return targetSec;
    for (let i = 0; i < b.length; i++) {
      if (targetSec >= b.start(i) && targetSec <= b.end(i)) return targetSec;
    }
    const lastEnd = b.end(b.length - 1);
    if (targetSec > lastEnd) return Math.max(b.start(b.length - 1), lastEnd - 0.3);
  } catch (_) {
    /* detached element */
  }
  return targetSec;
}

// Some sources cannot seek (no range support): a seek restarts them at 0.
function canSeekTo(el, targetSec) {
  try {
    const r = el.seekable;
    for (let i = 0; i < r.length; i++) {
      if (targetSec >= r.start(i) - 0.1 && targetSec <= r.end(i) + 0.1) return true;
    }
  } catch (_) {
    /* detached element */
  }
  return false;
}

export const createVideoSyncState = () => ({ driftStreak: 0, nudging: false, lead: START_LEAD });

// Stops frame tracking; call when the video goes away.
export function releaseVideoSync(sync) {
  const el = sync.el;
  if (el && sync.frameHandle != null && typeof el.cancelVideoFrameCallback === "function") {
    el.cancelVideoFrameCallback(sync.frameHandle);
  }
  Object.assign(sync, {
    el: null,
    watching: false,
    frame: null,
    frameHandle: null,
    playPending: false,
    learnLead: false,
    nudging: false,
    driftStreak: 0,
    frontierSeekAt: null,
    driftAt: null,
  });
}

const setRate = (el, rate) => {
  if (el.playbackRate !== rate) el.playbackRate = rate;
};

// Call on every scene frame while the video should be playing. `maxSec` is
// the end of the clip (trim end); the video is never moved past it.
// Returns "nudge" | "seek" | null for debug counters.
export function driveVideoSync(sync, el, targetSec, baseRate = 1, maxSec = null) {
  const base = Number(baseRate) > 0 ? Number(baseRate) : 1;
  if (sync.el !== el) {
    // The <video> was re-created: track the new element (the lead is kept).
    releaseVideoSync(sync);
    sync.el = el;
  }

  if (el.ended) {
    sync.driftStreak = 0;
    sync.nudging = false;
    sync.driftAt = null;
    setRate(el, base);
    // Normally hold the last frame. A target back inside the clip (the scene
    // was seeked back) rewinds it; the paused branch then starts it again.
    const end = Number.isFinite(el.duration) ? el.duration : null;
    if (end != null && targetSec < end - SEEK_AT && canSeekTo(el, targetSec)) {
      el.currentTime = clampSeekToBuffered(el, targetSec);
    }
    return null;
  }
  if (el.readyState < 3 /* HAVE_FUTURE_DATA */ || el.seeking) {
    sync.driftStreak = 0;
    sync.nudging = false;
    sync.driftAt = null;
    setRate(el, base);
    return null;
  }

  const now = performance.now();
  if (el.paused) {
    // A play() that has not settled yet is still starting this video.
    if (sync.playPending && now - sync.startedAt < START_WAIT_MS) return null;
    sync.learnLead = false;
    if (Math.abs(el.currentTime - targetSec) > START_TOLERANCE) {
      // A seek is needed anyway (and invisible while paused): land ahead by
      // the measured play() delay. Otherwise just play and catch up — an
      // extra seek can raise "waiting" and trip the buffer gate.
      let startAt = targetSec + sync.lead * base;
      if (maxSec != null) startAt = Math.max(targetSec, Math.min(startAt, maxSec - FRAME));
      if (canSeekTo(el, startAt)) {
        el.currentTime = clampSeekToBuffered(el, startAt);
        sync.learnLead = true;
      } else if (el.currentTime - targetSec > SEEK_AT) {
        return null; // cannot seek back: wait for the clock to arrive
      }
    }
    sync.driftStreak = 0;
    sync.nudging = false;
    sync.driftAt = null;
    sync.startedAt = now;
    sync.frame = null; // the lead is learned from the first frame of this start
    setRate(el, base);
    sync.playPending = true;
    const settle = () => {
      if (sync.el === el) sync.playPending = false;
    };
    Promise.resolve(el.play()).then(settle, settle);
    return null;
  }

  const drift = presentedTime(sync, el) - targetSec; // > 0: video ahead
  const abs = Math.abs(drift);
  if (sync.learnLead && sync.frame && now - sync.startedAt > 150) {
    // First frames after a start show how far off the lead was.
    sync.learnLead = false;
    if (abs < SEEK_AT) sync.lead = Math.max(0, Math.min(MAX_LEAD, sync.lead - drift / base));
  }

  if (abs > SEEK_AT && canSeekTo(el, targetSec)) {
    sync.nudging = false;
    sync.driftAt = null;
    if (++sync.driftStreak < SEEK_AFTER_TICKS) return null;
    sync.driftStreak = 0;
    setRate(el, base);
    const clamped = clampSeekToBuffered(el, targetSec);
    if (clamped < targetSec - 0.5) {
      // Target is past the download frontier: align to it once, then wait
      // until the frontier itself has moved.
      if (sync.frontierSeekAt != null && clamped - sync.frontierSeekAt < 1) return null;
      sync.frontierSeekAt = clamped;
    } else {
      sync.frontierSeekAt = null;
    }
    el.currentTime = clamped;
    return "seek";
  }

  if (abs > SEEK_AT && drift > 0) {
    el.pause(); // unseekable and ahead: hold this frame until the clock arrives
    return null;
  }

  sync.driftStreak = 0;
  sync.frontierSeekAt = null;
  const dt = sync.driftAt == null ? null : (now - sync.driftAt) / 1000;
  sync.smooth =
    dt == null || dt > 0.25
      ? drift
      : sync.smooth + (drift - sync.smooth) * (1 - Math.exp(-dt / SMOOTH_SEC));
  sync.driftAt = now;
  const avg = sync.smooth;
  if (Math.abs(avg) <= (sync.nudging ? SETTLED : IN_SYNC)) {
    sync.nudging = false;
    setRate(el, base);
    return null;
  }
  // In wall time, so the drift closes equally fast at every base rate.
  const wallDrift = avg / base;
  const limit = Math.abs(wallDrift) > FAST_AT ? FAST_NUDGE : MAX_NUDGE;
  const adjust = Math.max(-limit, Math.min(limit, -wallDrift / CATCH_UP_SEC));
  const rounded = Math.round(adjust / RATE_STEP) * RATE_STEP || (avg < 0 ? RATE_STEP : -RATE_STEP);
  const step = Math.max(-limit, Math.min(limit, rounded));
  const rate = +(base * (1 + step)).toFixed(4);
  const started = !sync.nudging;
  sync.nudging = true;
  setRate(el, rate);
  return started ? "nudge" : null;
}
